import {
  Fragment,
} from 'react';
import classNames from 'classnames';
import {
  useSettings,
} from '@dinosave/core/stores';
import type {
  ItemEntity,
} from '@dinosave/core/types';
import {
  formatPrice,
} from '@dinosave/core/utils';
import type {
  TextProps,
} from '@/ui/components/Text';
import Text from '@/ui/components/Text';

import styles from './ItemPrice.module.css';

const DEFAULT_PRIMARY_KIND = 'control';
const DEFAULT_PRIMARY_SIZE = 'large';
const DEFAULT_PRIMARY_WEIGHT = '600';
const DEFAULT_PRIMARY_COLOR = 'primary-700';
const DEFAULT_SECONDARY_KIND = 'control';
const DEFAULT_SECONDARY_SIZE = 'small';
const DEFAULT_SECONDARY_WEIGHT = '400';
const DEFAULT_SECONDARY_COLOR = 'neutral-500';

type ItemPriceProps = {
  item: ItemEntity;
  primaryKind?: TextProps['kind'];
  primarySize?: TextProps['size'];
  primaryColor?: TextProps['color'];
  primaryWeight?: TextProps['weight'];
  secondaryKind?: TextProps['kind'];
  secondarySize?: TextProps['size'];
  secondaryColor?: TextProps['color'];
  secondaryWeight?: TextProps['weight'];
  verbose?: boolean;
};

const ItemPrice = (props: ItemPriceProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    item,
    primaryKind = DEFAULT_PRIMARY_KIND,
    primarySize = DEFAULT_PRIMARY_SIZE,
    primaryColor = DEFAULT_PRIMARY_COLOR,
    primaryWeight = DEFAULT_PRIMARY_WEIGHT,
    secondaryKind = DEFAULT_SECONDARY_KIND,
    secondarySize = DEFAULT_SECONDARY_SIZE,
    secondaryColor = DEFAULT_SECONDARY_COLOR,
    secondaryWeight = DEFAULT_SECONDARY_WEIGHT,
    verbose = false,
  } = props;

  const {
    currency,
    previousPrice,
    currentPrice,
  } = item || {};

  // ##########################################################################
  // STORES
  // ##########################################################################

  const settings = useSettings(s => s.settings);
  const webItemsView = settings?.webItemsView || 'grid';

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const itemPriceClassName = classNames({
    [styles.itemPrice]: true,
    [styles.verbose]: verbose,
    [styles.numeric]: !verbose,
    [styles.grid]: webItemsView === 'grid',
    [styles.list]: webItemsView === 'list',
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  if (
    !currentPrice &&
    !previousPrice
  ) return null;

  return (
    <div className={itemPriceClassName}>
      {currentPrice && previousPrice && (
        <Fragment>
          <div className={styles.current}>
            <Text
              kind={primaryKind}
              size={primarySize}
              color={primaryColor}
              weight={primaryWeight}
            >
              {formatPrice(currentPrice, currency)}
            </Text>
          </div>
          <div className={styles.previous}>
            <Text
              kind={secondaryKind}
              size={secondarySize}
              color={secondaryColor}
              weight={secondaryWeight}
            >
              <span className={styles.prefix}>
                <Text
                  kind={secondaryKind}
                  size={secondarySize}
                  color={secondaryColor}
                  weight={secondaryWeight}
                >
                  {verbose && 'was '}
                </Text>
              </span>
              {formatPrice(previousPrice, currency)}
            </Text>

          </div>
        </Fragment>
      )}

      {currentPrice && !previousPrice && (
        <div className={styles.current}>
          <Text
            kind={primaryKind}
            color="neutral-600"
            weight={primaryWeight}
          >
            {formatPrice(currentPrice, currency)}
          </Text>
        </div>
      )}
    </div>
  );
};

export default ItemPrice;
